import { lazy, ComponentType } from "react";
import { isStorageEphemeral } from "./storageFallback";

type ComponentImport<T> = () => Promise<{ default: T }>;

/**
 * Checks if an error is a chunk loading failure (stale deployment).
 * These occur when cached HTML references chunk files that no longer exist.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk") ||
    message.includes("dynamically imported module") ||
    message.includes("importing a module script failed") || // Safari/iOS
    message.includes("failed to fetch")
  );
}

/**
 * Wrapper for React.lazy that handles chunk loading failures from stale deployments.
 *
 * When a deployment creates new chunk hashes and a user has cached HTML pointing
 * to old chunks, the import will fail with a 404. This wrapper:
 * 1. Attempts the import
 * 2. On failure, retries once after a short delay
 * 3. On second failure, triggers a full page reload to get fresh HTML
 *
 * @param importFn - The dynamic import function, e.g., () => import("./MyComponent")
 * @param retries - Number of retry attempts before forcing reload (default: 1)
 */
// Constraint uses ComponentType<any> (as React.lazy itself does): components with
// REQUIRED props are ComponentType<TheirProps>, which is contravariantly NOT
// assignable to ComponentType<unknown>. `any` is the standard escape hatch so any
// lazily-loaded route/view component is accepted regardless of its props shape.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own ComponentType<any> signature; unknown breaks components with required props
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: ComponentImport<T>,
  retries: number = 1
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Small delay before retry to allow any cache invalidation
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        return await importFn();
      } catch (error) {
        lastError = error;

        // Only retry on chunk loading errors, not other errors
        if (!isChunkLoadError(error)) {
          throw error;
        }

        console.warn(
          `[lazyWithRetry] Import failed (attempt ${attempt + 1}/${retries + 1}):`,
          error
        );
      }
    }

    // All retries exhausted - reload page to get fresh assets
    if (isChunkLoadError(lastError)) {
      console.warn(
        "[lazyWithRetry] All retries failed, reloading page to get fresh assets"
      );

      // Store flag to prevent infinite reload loop
      const reloadKey = "lazyWithRetry_reload";
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();

      // sessionStorage.setItem below doesn't survive the very reload it's meant
      // to gate when it's our in-memory fallback (browser forbids real storage,
      // Sentry JAVASCRIPT-REACT-58): the loop guard would never see a stamp, so
      // never auto-reload in that case, to avoid reloading forever.
      if ((!lastReload || now - parseInt(lastReload, 10) > 10000) && !isStorageEphemeral("sessionStorage")) {
        // More than 10 seconds since last reload, safe to reload
        sessionStorage.setItem(reloadKey, now.toString());
        window.location.reload();

        // Return a never-resolving promise to prevent rendering during reload
        return new Promise(() => {});
      } else {
        // Recent reload didn't fix it, something else is wrong
        console.error(
          "[lazyWithRetry] Recent reload did not resolve the issue"
        );
      }
    }

    // Re-throw the last error
    throw lastError;
  });
}
